import React from "react";
import { useTranslation } from "react-i18next";
import { U2000_AGING_BUCKETS, type U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import { u3125FormatCurrency, NS } from "../../U3125";
import { u3125ArAgingBucketMap, type U3125ArAgingBucketSummary, type U3125ArAgingTotals } from "../../U3125Service";

export interface U3125ArAgingBucketTableProps {
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
  showTotalsColumn?: boolean;
  showHeader?: boolean;
  /** Tranche filtrée (surbrillance colonne). */
  selectedBucket?: U2000AgingBucket | null;
  /** En-têtes de tranche cliquables pour filtrer (tableau organisation). */
  linkableBuckets?: boolean;
  onSelectBucket?: (bucket: U2000AgingBucket | null) => void;
  variant?: "default" | "compact";
}

export function u3125ArAgingBucketColumnClass(
  code: U2000AgingBucket,
  selectedBucket: U2000AgingBucket | null | undefined,
): string {
  return selectedBucket === code ? "bg-primary-50/80" : "";
}

export interface U3125ArAgingBalanceWithInvoiceCountProps {
  balanceDue: number;
  invoiceCount: number;
  countClassName?: string;
}

const u3125ArAgingCountColumnClass = "inline-block min-w-7 shrink-0 pl-1 text-right";

export interface U3125ArAgingBucketColumnCellLayoutProps {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  primaryClassName?: string;
  secondaryClassName?: string;
  reserveCountColumn?: boolean;
  tabular?: boolean;
}

export const U3125ArAgingBucketColumnCellLayout: React.FC<U3125ArAgingBucketColumnCellLayoutProps> = ({
  primary,
  secondary,
  primaryClassName = "",
  secondaryClassName = "text-muted-foreground",
  reserveCountColumn = true,
  tabular = true,
}) => (
  <span className={`flex w-full items-baseline justify-end gap-1 ${tabular ? "tabular-nums" : ""}`}>
    <span className={`text-right whitespace-nowrap ${primaryClassName}`}>{primary}</span>
    {reserveCountColumn ? (
      secondary != null ? (
        <span className={`${u3125ArAgingCountColumnClass} ${secondaryClassName}`}>{secondary}</span>
      ) : (
        <span className={`${u3125ArAgingCountColumnClass} invisible select-none`} aria-hidden>
          &#8203;
        </span>
      )
    ) : null}
  </span>
);

export const U3125ArAgingBalanceWithInvoiceCount: React.FC<U3125ArAgingBalanceWithInvoiceCountProps> = ({
  balanceDue,
  invoiceCount,
  countClassName = "text-muted-foreground",
}) => (
  <U3125ArAgingBucketColumnCellLayout
    primary={u3125FormatCurrency(balanceDue)}
    secondary={`(${invoiceCount})`}
    secondaryClassName={countClassName}
  />
);

const u3125ArAgingBucketHeaderCellClass = "pb-1 px-1 text-right font-medium whitespace-nowrap";
const u3125ArAgingBucketBodyCellClass = "px-1 text-right";

export interface U3125ArAgingBucketTableHeaderProps {
  showTotalsColumn?: boolean;
  selectedBucket?: U2000AgingBucket | null;
  linkableBuckets?: boolean;
  onSelectBucket?: (bucket: U2000AgingBucket | null) => void;
  alignPeriodWithAmountLineCount?: boolean;
}

function u3125ArAgingBucketHeaderCellContent(
  label: React.ReactNode,
  alignPeriodWithAmountLineCount: boolean,
  primaryClassName = "",
): React.ReactNode {
  if (!alignPeriodWithAmountLineCount) return label;
  return (
    <U3125ArAgingBucketColumnCellLayout
      primary={label}
      primaryClassName={primaryClassName}
      tabular={false}
    />
  );
}

export const U3125ArAgingBucketTableHeader: React.FC<U3125ArAgingBucketTableHeaderProps> = ({
  showTotalsColumn = true,
  selectedBucket = null,
  linkableBuckets = false,
  onSelectBucket,
  alignPeriodWithAmountLineCount = false,
}) => {
  const { t } = useTranslation();

  const handleBucketClick = (code: U2000AgingBucket) => {
    if (!linkableBuckets || !onSelectBucket) return;
    onSelectBucket(selectedBucket === code ? null : code);
  };

  const bucketHeaderButtonClass = alignPeriodWithAmountLineCount
    ? "block w-full text-right text-primary-600 hover:underline font-medium"
    : "text-primary-600 hover:underline font-medium";

  return (
    <thead>
      <tr className="text-left text-muted-foreground">
        <th className="pb-1 pr-2 font-medium whitespace-nowrap" />
        {U2000_AGING_BUCKETS.map((code) => {
          const label = t(`arAging.bucket.${code}`, { ns: NS, defaultValue: code });
          const colClass = `${u3125ArAgingBucketHeaderCellClass} ${u3125ArAgingBucketColumnClass(code, selectedBucket)}`;
          const cellContent = u3125ArAgingBucketHeaderCellContent(label, alignPeriodWithAmountLineCount);
          if (linkableBuckets && onSelectBucket) {
            return (
              <th key={code} className={colClass}>
                <button
                  type="button"
                  className={bucketHeaderButtonClass}
                  onClick={() => handleBucketClick(code)}
                  title={t("lblArAgingFilterByBucket", { ns: NS, bucket: label })}
                >
                  {cellContent}
                </button>
              </th>
            );
          }
          return (
            <th key={code} className={colClass}>
              {cellContent}
            </th>
          );
        })}
        {showTotalsColumn ? (
          <th className="pb-1 pl-2 text-right font-medium whitespace-nowrap">
            {linkableBuckets && onSelectBucket && selectedBucket ? (
              <button
                type="button"
                className={bucketHeaderButtonClass}
                onClick={() => onSelectBucket(null)}
                title={t("btnArAgingClearBucketFilter", { ns: NS })}
              >
                {u3125ArAgingBucketHeaderCellContent(
                  t("lblArAgingTotal", { ns: NS }),
                  alignPeriodWithAmountLineCount,
                )}
              </button>
            ) : (
              u3125ArAgingBucketHeaderCellContent(
                t("lblArAgingTotal", { ns: NS }),
                alignPeriodWithAmountLineCount,
              )
            )}
          </th>
        ) : null}
      </tr>
    </thead>
  );
};

export interface U3125ArAgingBucketMetricRowsProps {
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
  showTotalsColumn?: boolean;
  selectedBucket?: U2000AgingBucket | null;
  variant?: "default" | "compact";
  layout?: "dualRow" | "singleRow";
  countLabel?: string;
  amountLabel?: string;
  countLabelClassName?: string;
  amountLabelClassName?: string;
  firstDetailRow?: boolean;
}

export const U3125ArAgingBucketMetricRows: React.FC<U3125ArAgingBucketMetricRowsProps> = ({
  buckets,
  totals,
  showTotalsColumn = true,
  selectedBucket = null,
  variant = "default",
  layout = "dualRow",
  countLabel,
  amountLabel,
  countLabelClassName = "",
  amountLabelClassName = "",
  firstDetailRow = false,
}) => {
  const { t } = useTranslation();
  const bucketMap = u3125ArAgingBucketMap(buckets);
  const rowPy = variant === "compact" ? "py-0.5" : "py-1";
  const resolvedCountLabel = countLabel ?? t("colArAgingInvoiceCount", { ns: NS });
  const resolvedAmountLabel = amountLabel ?? t("colArAgingBalanceDue", { ns: NS });
  const detailBorder = firstDetailRow ? "border-t-2 border-gray-200" : "border-t border-gray-100";

  if (layout === "singleRow") {
    return (
      <tr className={firstDetailRow ? detailBorder : "border-t border-gray-100"}>
        <td className={`${rowPy} pr-2 font-medium whitespace-nowrap ${countLabelClassName}`}>
          {resolvedCountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => {
          const bucket = bucketMap.get(code);
          return (
            <td
              key={`single-${code}`}
              className={`${rowPy} ${u3125ArAgingBucketBodyCellClass} ${u3125ArAgingBucketColumnClass(code, selectedBucket)}`}
            >
              <U3125ArAgingBalanceWithInvoiceCount
                balanceDue={bucket?.balanceDue ?? 0}
                invoiceCount={bucket?.invoiceCount ?? 0}
              />
            </td>
          );
        })}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right font-medium`}>
            <U3125ArAgingBalanceWithInvoiceCount
              balanceDue={totals.balanceDue ?? 0}
              invoiceCount={totals.invoiceCount ?? 0}
            />
          </td>
        ) : null}
      </tr>
    );
  }

  return (
    <>
      <tr className={firstDetailRow ? detailBorder : "border-t border-gray-100"}>
        <td className={`${rowPy} pr-2 font-medium whitespace-nowrap ${countLabelClassName}`}>
          {resolvedCountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => (
          <td
            key={`count-${code}`}
            className={`${rowPy} px-1 text-right tabular-nums ${u3125ArAgingBucketColumnClass(code, selectedBucket)}`}
          >
            {bucketMap.get(code)?.invoiceCount ?? 0}
          </td>
        ))}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right tabular-nums font-medium`}>{totals.invoiceCount ?? 0}</td>
        ) : null}
      </tr>
      <tr className="border-t border-gray-100">
        <td className={`${rowPy} pr-2 font-medium text-muted-foreground whitespace-nowrap ${amountLabelClassName}`}>
          {resolvedAmountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => (
          <td
            key={`bal-${code}`}
            className={`${rowPy} px-1 text-right tabular-nums ${u3125ArAgingBucketColumnClass(code, selectedBucket)}`}
          >
            {u3125FormatCurrency(bucketMap.get(code)?.balanceDue ?? 0)}
          </td>
        ))}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right tabular-nums font-medium`}>
            {u3125FormatCurrency(totals.balanceDue ?? 0)}
          </td>
        ) : null}
      </tr>
    </>
  );
};

const U3125ArAgingBucketTable: React.FC<U3125ArAgingBucketTableProps> = ({
  buckets,
  totals,
  showTotalsColumn = true,
  showHeader = true,
  selectedBucket = null,
  linkableBuckets = false,
  onSelectBucket,
  variant = "default",
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-160 text-xs">
        {showHeader ? (
          <U3125ArAgingBucketTableHeader
            showTotalsColumn={showTotalsColumn}
            selectedBucket={selectedBucket}
            linkableBuckets={linkableBuckets}
            onSelectBucket={onSelectBucket}
          />
        ) : null}
        <tbody>
          <U3125ArAgingBucketMetricRows
            buckets={buckets}
            totals={totals}
            showTotalsColumn={showTotalsColumn}
            selectedBucket={selectedBucket}
            variant={variant}
          />
        </tbody>
      </table>
    </div>
  );
};

export default U3125ArAgingBucketTable;
